/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { type ButtonProps, OverlayTrigger, Popover } from 'react-bootstrap';
// import { updateNote } from '../../../api/notes'
import { getGlobalState } from '../../../redux';
import { useSingleStringUrlParameter } from '../../../hooks/common/use-single-string-url-parameter';
import { IconButton } from '../../common/icon-button/icon-button';
import { Plus as IconPlus } from 'react-bootstrap-icons';
import { useUiNotifications } from '../../notifications/ui-notification-boundary';
import { useApplicationState } from '@hooks/common/use-application-state';
import {
  useFetchVersionsListQuery,
  useUpdateDraftContentMutation,
  versionHistoryApi
} from '@services/versionHistoryService';
import ConfirmationModal from '@components/common/modals/confirmation-modal';
import { PublishFooter, PublishMessage } from './publish-modal-ui';
import { useRevalidateArticle } from '@hooks/common/use-revalidate-article';
import { setEditorDocChange } from '@redux/editor/methods';
import { useDispatch } from 'react-redux';
import { setDraftContent } from '@redux/note-draft/noteDraft';

export type PublishButtonProps = Omit<ButtonProps, 'onClick'> & {
  setArticleDraftLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isArticlePublished: boolean;
}
/**
 * Renders a button that opens a settings modal.
 */
export const PublishButton: React.FC<PublishButtonProps> = ({ setArticleDraftLoading, isArticlePublished }) => {
  const [updateDraftContenTrigger, {
    isLoading: isSavingDraftContent,
  }] = useUpdateDraftContentMutation();
  const { dispatchUiNotification } = useUiNotifications();
  const { count: formatIssuesCount, draftMode } = useApplicationState(
    (state) => ({
      count: state.formatChecker.count,
      draftMode: state.noteDetails.draftMode,
    })
  );
  const { markdownContent } = useApplicationState(
    (state) => ({
      markdownContent: state.noteDetails.markdownContent.plain,
      publishedAt: state.noteDetails.publishedAt,
    })
  );
  const id = useSingleStringUrlParameter('noteId', '');
  const { revalidateArticleCallback } = useRevalidateArticle(['', id]); // '' empty to revalidate home page
  const dispatch = useDispatch();

  const [
    saveArticleContentTrigger,
    { isLoading: isSavingArticleContent, error: saveArticleContentError }
  ] = versionHistoryApi.endpoints.updateArticleContent.useMutation();

  const { currentData: versionsListData } = useFetchVersionsListQuery({
    entitySlug: id as string
  });
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    setArticleDraftLoading(isSavingDraftContent);
  }, [isSavingDraftContent]);

  const updateArticleContent = useCallback(() => {
    const content = getGlobalState().noteDetails.markdownContent.plain;
    saveArticleContentTrigger({
      noteIdOrAlias: id as string,
      content
    })
      .unwrap()
      .then((res) => {
        if (res.success) {
          setEditorDocChange(false);
          dispatchUiNotification(
            'Article Save Success',
            'Article Saved Successfully',
            {}
          );
          revalidateArticleCallback()
        } else {
          dispatchUiNotification(
            'Article Save Error',
            'Error while saving article',
            {}
          );
        }
      })
      .catch((err) => {
        dispatchUiNotification(
          'Article Save Error',
          err.message ?? err.data.error,
          {}
        );
      });
  }, [
    id,
    saveArticleContentTrigger,
    dispatchUiNotification,
    saveArticleContentError
  ]);

  const updateDraftContent = useCallback(() => {
    const draftContent = getGlobalState().noteDraft.content;
    const articleContent = getGlobalState().noteDetails.markdownContent.plain;
    const content = draftContent !== null ? draftContent : articleContent;
    updateDraftContenTrigger({
      noteIdOrAlias: id as string,
      content,
    })
      .unwrap()
      .then((res) => {
        if (res.success) {
          setEditorDocChange(false);
          dispatch(setDraftContent(content));
          dispatchUiNotification(
            'Article Draft Save Success',
            'Article draft Saved Successfully',
            {}
          );
        } else {
          dispatchUiNotification(
            'Article Draft Save Error',
            'Error while saving article draft',
            {}
          );
        }
      })
      .catch((err) => {
        dispatchUiNotification(
          'Article Draft Save Error',
          err.message ?? err.data.error,
          {}
        );
      });
  }, [
    id,
    dispatchUiNotification,
  ])

  const getTooltipMessage = useMemo(() => {
    if (formatIssuesCount > 0) {
      return 'Fix all format issues before saving note.';
    } else if (versionsListData?.versionsCount === 10) {
      return 'You have reached the maximum versions limit. Please delete a version before saving.';
    } else {
      return '';
    }
  }, [formatIssuesCount, versionsListData?.versionsCount]);

  const popover = (
    <Popover id='popover'>
      <Popover.Header as='h3'>Advice</Popover.Header>
      <Popover.Body as='h6'>{getTooltipMessage}</Popover.Body>
    </Popover>
  );

  const handleSaveButton = useCallback(() => {
    if(draftMode) {
      if (isArticlePublished) {
        updateDraftContent();
      }
    } else {
      if (isArticlePublished) {
        setShowConfirmation(true)
      } else {
        updateArticleContent()
      }
    }
  }, [
    draftMode,
    isArticlePublished,
    updateDraftContent,
    updateArticleContent,
  ]);

  const getDraftCtaText = () => {
    if (isSavingDraftContent) {
      return ('Saving Article as Draft')
    }
    return ('Save Article as Draft');
  }

  const SaveButton = () => (
    <IconButton
      className={cn({
        'btn-secondary': versionsListData?.versionsCount === 10,
        'btn-danger text-light': markdownContent.length === 0,
      })}
      disabled={
        markdownContent.length === 0 ||
        isSavingArticleContent || isSavingDraftContent ||
        versionsListData?.versionsCount === 10
      }
      iconSize={2}
      size={'lg'}
      icon={IconPlus}
      onClick={handleSaveButton}>
      {draftMode ? 
        getDraftCtaText()
      : (isSavingArticleContent ? 'Saving Article' : 'Save Article')}
    </IconButton>
  );

  const OverlayButton = () => (
    <OverlayTrigger placement='bottom' overlay={popover}>
      <div>
        <SaveButton />
      </div>
    </OverlayTrigger>
  );

  return (
    <>
      {getTooltipMessage !== '' ? <OverlayButton  /> : <SaveButton />}
      <ArticlePublishConfirmationModal
        onConfirm={updateArticleContent}
        showConfirmation={showConfirmation}
        setShowConfirmation={setShowConfirmation}
      />
    </>
  );
};

export const ArticlePublishConfirmationModal = ({
  onConfirm,
  showConfirmation,
  setShowConfirmation
}: {
  onConfirm(): void;
  showConfirmation: boolean;
  setShowConfirmation: Function;
}) => {
  const id = useSingleStringUrlParameter('noteId', '');
  const { userData } = useApplicationState((state) => ({
    userData: state.user
  }));

  return (
    <ConfirmationModal
      title='Publish Article'
      onConfirm={onConfirm}
      message={
        <PublishMessage
          noteDetail={{
            noteAlias: id,
            username: userData?.username
          }}
        />
      }
      show={showConfirmation}
      footerUi={
        <PublishFooter
          publishArticle={() => {
            onConfirm();
            setShowConfirmation(false);
          }}
        />
      }
      footerClassName={'justify-content-center w-100'}
      onHide={() => setShowConfirmation(false)}
      showCloseButton
    />
  );
};
